import { Component, Input, Output, EventEmitter, AfterViewInit, ViewChild, OnInit } from '@angular/core';
import { DataWidgetI, WidgetCache, WidgetIds, UserWidget } from '../../dashboard.model';
import { Observable, Subject } from 'rxjs';
import { PortfolioAnalysisWidgetModel, PortfolioAnalysisAnnualData } from './portfolio.analysis.widget.model';
import * as _ from 'lodash';
import { PortfolioAnalysisChartTableComponent } from './portfolio.analysis.chart.table.component';
import { DashboardDataService } from '../../dashboard.data.service';

@Component({
    selector: 'portfolio-analysis-widget',
    templateUrl: './portfolio.analysis.widget.component.html',
    styleUrls: ['./portfolio.analysis.widget.component.scss'],
    standalone: false,
})
export class PortfolioAnalysisWidgetComponent implements DataWidgetI, AfterViewInit, OnInit {
    constructor(private readonly _dashboardDataService: DashboardDataService) {}

    @ViewChild(PortfolioAnalysisChartTableComponent)
    chartTableComponent: PortfolioAnalysisChartTableComponent;

    @Input() data$: Observable<any>;
    @Input() userWidget: UserWidget;
    @Output() newParams: EventEmitter<object> = new EventEmitter();

    // The values of this object are attribute names in the TotalValueAttributes array objects
    // corresponding to chartType passed to lineItemClicked()
    TotalValueNames: any = {
        TOTALFMV: 'totalFMV',
        FMVPERSQFT: 'fmvPerSqFt',
        FMVPERUNIT: 'fmvPerUnit',
        LANDFMVPERSQFT: 'landFMVPerSqFt',
        LANDFMVPERACRE: 'landFMVPerAcre',
    };
    // The keys of this object align with the values of the object above.
    TotalValueAttributes: any = {
        totalFMV: { title: 'Total MV:' },
        fmvPerSqFt: { title: 'Total MV $/SqFt:' },
        fmvPerUnit: { title: 'Total MV $/Unit:' },
        landFMVPerSqFt: { title: 'Land MV $/SqFt:' },
        landFMVPerAcre: { title: 'Land MV $/Acre:' },
    };

    resize$: Subject<void>;
    availableYears: number[];
    selectedYear: number;

    data: PortfolioAnalysisWidgetModel;

    currentYear: PortfolioAnalysisAnnualData;
    acres: string;
    impsSqFt: string;
    units: string;

    WidgetIds = WidgetIds;

    get isChartWidget(): boolean {
        return this.userWidget.widget.widgetId === WidgetIds.PortfolioAnalysis;
    }

    ngOnInit(): void {
        // Initialize selected year to current year upon first display.
        this.selectedYear = new Date().getFullYear();

        this.resize$ = new Subject<void>();
        // WK-8048 - The chart doesn't get automatically updated when we resize the widget for some reason;
        // "kick" the widget when resized in design mode to make it work correctly
        this._dashboardDataService.userWidgetResize$.subscribe(() => this.resize$.next());
    }

    ngAfterViewInit(): void {
        this.data$.subscribe((widgetCache: WidgetCache) => {
            this.data = widgetCache.data;
            this.availableYears = _.reverse(this.data.taxYears);

            this.currentYear = _.findLast(this.data.annualData, (x) => x.year <= this.selectedYear);
            if (!this.currentYear) {
                // Didn't find selected year or an earlier year.
                // Look for a later year than selected.
                this.currentYear = _.find(this.data.annualData, (x) => x.year > this.selectedYear);
                if (!this.currentYear) {
                    // Didn't find any years.
                    // Create currentYear with default data for selected year.
                    this.currentYear = new PortfolioAnalysisAnnualData();
                    this.currentYear.year = this.selectedYear;
                }
            }
            if (this.currentYear.year != this.selectedYear) {
                // Didn't find selected year, but found an earlier/later year.
                this.selectedYear = this.currentYear.year;
            }

            this.currentYear.totalFMV = Math.round(this.currentYear.totalFMV);

            /*
            this.acres = _.find(this.data.propertyCharacteristics, {label: 'Land Acreage' });
            this.impsSqFt = _.find(this.data.propertyCharacteristics, {label: 'Imps. SqFt' });
            this.units = _.find(this.data.propertyCharacteristics, {label: 'Units' });
            */

            this.lineItemClicked('totalFMV');
        });
    }

    getDataByYear(year: number): void {
        this.newParams.emit({ year: year });
    }

    lineItemClicked(chartType: string): void {
        if (!this.isChartWidget) {
            return;
        }
        this.chartTableComponent.updateChartType(chartType, this.data);
    }

    getValueLabel(totalValueName: string): string {
        // Remove this if-block when TrueRateTaxableValues is permanently enabled
        return this.TotalValueAttributes[totalValueName] ? this.TotalValueAttributes[totalValueName].title : 'error!';
    }
}
